import {
  NEGATIVE_FILTER_TOKEN,
  SEPARATOR,
} from '@hakimo-ui/hakimo/data-access';
import {
  SystemNotificationPreference,
  TimePeriod,
  UnifiedAlarmGroup,
} from '@hakimo-ui/hakimo/types';
import {
  dismissToast,
  getMHFromTimePeriod,
  getStartEndTimes,
  toast,
  triggerSystemNotification,
} from '@hakimo-ui/hakimo/util';
import { NEW_ALARM_GROUP_TOAST_ID } from './constants';
import { AlarmGroupFilterValues } from './types';

export function getSearchParams(
  page: number,
  pageSize: number,
  timePeriod: TimePeriod,
  filterValues?: AlarmGroupFilterValues
): string {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));

  if (timePeriod !== TimePeriod.ALL_TIME) {
    if (timePeriod === TimePeriod.CUSTOM_RANGE && filterValues?.time) {
      const [startTime, endTime] = getStartEndTimes(filterValues.time);
      if (startTime) {
        params.append('dateFrom', new Date(startTime).getTime().toString());
      }
      if (endTime) {
        params.append('dateTo', new Date(endTime).getTime().toString());
      }
    } else {
      const [minutes, hours] = getMHFromTimePeriod(timePeriod);
      const totalMinutes = minutes + hours * 60;
      if (totalMinutes > 0) {
        params.append('relativeTimeMinutes', String(totalMinutes));
      }
    }
  }

  if (filterValues) {
    if (filterValues.status && filterValues.status.values.length > 0) {
      const statusQuery: string[] = [...filterValues.status.values];
      const query = getFilterQuery(statusQuery, filterValues.status.negative);
      params.append('state', query);
    }

    if (filterValues.priority && filterValues.priority.values.length > 0) {
      const priorityQuery: string[] = [...filterValues.priority.values];
      const query = getFilterQuery(
        priorityQuery,
        filterValues.priority.negative
      );
      params.append('priority', query);
    }

    if (filterValues.location && filterValues.location.values.length > 0) {
      const locationQuery: string[] = filterValues.location.values.map(
        (item: { id: string }) => item.id
      );
      const query = getFilterQuery(
        locationQuery,
        filterValues.location.negative
      );
      params.append('locationIds', query);

      const tenantIds = [
        ...new Set(
          filterValues.location.values
            .map((item) => item.tenant_id)
            .filter((id): id is string => Boolean(id))
        ),
      ];
      if (tenantIds.length > 0) {
        params.append('tenantIds', tenantIds.join(SEPARATOR));
      }
    }

    if (filterValues.camera && filterValues.camera.values.length > 0) {
      const cameraQuery: string[] = filterValues.camera.values.map(
        (item: { id: string }) => item.id
      );
      const query = getFilterQuery(cameraQuery, filterValues.camera.negative);
      params.append('cameraIds', query);
    }

    if (filterValues.escalated === true) {
      params.append('isEscalated', 'true');
    }
  }
  return params.toString();
}

function getFilterQuery(values: string[], negative: boolean): string {
  const query = values;
  if (negative) {
    query.push(NEGATIVE_FILTER_TOKEN);
  }
  return query.join(SEPARATOR);
}

const getPendingAlarmGroupCount = (data?: UnifiedAlarmGroup[]): number => {
  return (
    data?.filter((item) => item.status.toLowerCase() === 'pending').length || 0
  );
};

export function notifyNewAlarmGroup(
  prevData: UnifiedAlarmGroup[],
  newData: UnifiedAlarmGroup[],
  page: number,
  systemNotifyPreference: SystemNotificationPreference,
  audio: boolean
): void {
  if (
    // old data had no pending
    getPendingAlarmGroupCount(prevData) === 0 &&
    // new data has pending alarm
    getPendingAlarmGroupCount(newData) > 0 &&
    // on first page
    page === 1
  ) {
    const notifAudioFileName = 'notif-sound-default';
    triggerSystemNotification(systemNotifyPreference, 'New Alarm Group');
    toast('New Alarm Group', {
      audio,
      id: NEW_ALARM_GROUP_TOAST_ID,
      audioFileName: notifAudioFileName,
    });
  } else if (getPendingAlarmGroupCount(newData) === 0) {
    dismissToast(NEW_ALARM_GROUP_TOAST_ID);
  }
}
